import React, { useEffect, useMemo, useState } from 'react'
import Modal from 'components/atoms/Modal'
import styled, { css } from 'styled-components'
import Input from 'components/atoms/Input'
import Select from 'components/atoms/Select'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentUserRole } from 'features/auth/ducks/selectors'
import { isNotNilOrEmpty } from 'utils/ramda'
import moment from 'moment'
import { propOr, splitEvery } from 'ramda'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import InputDescription from 'features/createBp/components/atoms/InputDescription'
import EnhancedFileTypeSelector from 'features/bpDetails/Components/files/EnhancedFileTypeSelector'
import { getFileTypes } from 'ducks/files/selectors'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { fetchBpRoutine } from 'features/bpDetails/ducks/actions'
import { loadAllFilesRoutine, getBpFilesCountsRoutine } from 'ducks/files/actions'
import { bulkEditFiles } from 'services/fileService'
import { toast } from 'react-toastify'
import Checkbox from 'components/atoms/Checkbox'

const emptyFileValues = {
  fileTypeId: null,
  producedBy: null,
  creationDate: null,
  legalOwner: null,
  expirationDate: null,
  approvedBy: null,
  expirationReminderDate: null,
  segment: null,
  fileCategory: null,
  dateOfIssue: null
}

const BulkEditFileModal = ({ fileIds = [], open, onClose, selectedFiles, bp: bpProp = null }) => {
  const safeSelectedFiles = selectedFiles || fileIds || []
  const dispatch = useDispatch()
  const [modalValues, setValues] = useState(emptyFileValues)
  const [wantsReminder, setWantsReminder] = useState(false)
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false)
  const [saving, setSaving] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })

  const fileTypes = useSelector(getFileTypes)
  const fileCategories = useSelector(selectFileCategories)
  const userRole = useSelector(getCurrentUserRole)
  const selectedBp = useSelector(getSelectedBp)
  const bp = bpProp ?? selectedBp
  const currentBpFiles = useSelector(state => state.files.currentBpFiles || [])

  const firstSelectedFile = useMemo(() => {
    return currentBpFiles.find(file => file.id === safeSelectedFiles?.[0])
  }, [selectedFiles, currentBpFiles])


  const handleValueChange = (name, value) => {
    if (name === 'expirationDate') {
      const subtractSixMonths = moment(value).subtract(6, 'month')
      const isBeforeToday = subtractSixMonths.isBefore(moment())
      const expirationReminderDate = isBeforeToday
        ? moment().add(1, 'day').format('YYYY-MM-DD')
        : subtractSixMonths.format('YYYY-MM-DD')

      setValues(prev => ({
        ...prev,
        [name]: value,
        expirationReminderDate
      }))
    } else {
      setValues(prev => ({ ...prev, [name]: value }))
    }
  }

  const handleClose = () => {
    setValues(emptyFileValues)
    setWantsReminder(false)
    setSaving(false)
    onClose()
  }

  // Auto-focus the file type selector when modal opens
  useEffect(() => {
    if (open) {
      setShouldAutoFocus(true)
    } else {
      setShouldAutoFocus(false)
    }
  }, [open])

  useEffect(() => {
    if (open && firstSelectedFile) {
      const version = firstSelectedFile.versions?.[0] || {}

      const expirationReminderDate = version.expirationReminderDate
        ? moment(version.expirationReminderDate).format('YYYY-MM-DD')
        : null

      setWantsReminder(Boolean(expirationReminderDate))

      setValues({
        fileTypeId: firstSelectedFile.fileType?.id || null,
        fileCategory: firstSelectedFile.fileType?.fileCategoryId || null,
        producedBy: version.producedBy || null,
        creationDate: version.creationDate
          ? moment(version.creationDate).format('YYYY-MM-DD')
          : null,
        legalOwner: version.legalOwner || null,
        expirationDate: version.expirationDate
          ? moment(version.expirationDate).format('YYYY-MM-DD')
          : null,
        approvedBy: version.approvedBy || null,
        expirationReminderDate,
        segment: version.segment || null,
        dateOfIssue: version.dateOfIssue
          ? moment(version.dateOfIssue).format('YYYY-MM-DD')
          : null
      })
    }
  }, [open, firstSelectedFile])


  const categoriesOptions = useMemo(() => {
    if (fileCategories) {
      const categories = fileCategories.map(type => ({
        label: type.name,
        value: type.id
      }))

      return [
        { label: 'All file types', value: 'all' },
        ...categories
      ]
    }
    return []
  }, [fileCategories, userRole])

  const typeOptions = useMemo(() => {
    if (fileTypes && modalValues.fileCategory) {
      const currentCategoryTypes =
        modalValues.fileCategory === 'all'
          ? fileTypes
          : fileTypes.filter(
            type => type.fileCategoryId === modalValues.fileCategory
          )

      const options = currentCategoryTypes.map(type => {
        const currentCategory = categoriesOptions.find(category => category.value === type.fileCategoryId)
        return {
          label: type.name,
          value: type.id,
          description: type.description,
          categoryName: propOr('', 'label', currentCategory),
          categoryId: propOr('', 'value', currentCategory)
        }
      })

      return userRole !== 'emergency_service'
        ? options.filter(option => option.label !== 'FRS floor plan')
        : options
    }
    return []
  }, [fileTypes, userRole, modalValues.fileCategory, categoriesOptions])

  const isValid = useMemo(() => {
    const { fileTypeId } = modalValues
    return isNotNilOrEmpty(fileTypeId)
  }, [modalValues])

  const handleFileTypeSelect = (categoryId, typeId) => {
    setValues(prev => ({
      ...prev,
      fileTypeId: typeId,
      fileCategory: categoryId
    }))
    // Reset auto-focus after selection
    setShouldAutoFocus(false)
  }

  const refreshFiles = () => {
    if (!bp?.id) return
    dispatch(fetchBpRoutine({ id: bp.id }))
    dispatch(loadAllFilesRoutine({ bpId: bp.id }))
    dispatch(getBpFilesCountsRoutine({ id: bp.id }))
  }

  const handleUpload = async e => {
    e.preventDefault()
    const transformToSnakeCase = obj => {
      const newObj = {}
      Object.keys(obj).forEach(key => {
        const snake = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
        newObj[snake] = obj[key]
      })
      return newObj
    }

    const updates = transformToSnakeCase({
      ...modalValues,
      expirationReminderDate: wantsReminder ? modalValues.expirationReminderDate : null
    })
    const fileIdList = safeSelectedFiles.map(f => (typeof f === 'string' ? f : f?.id)).filter(Boolean)
    const chunks = splitEvery(10, fileIdList)
    const total = fileIdList.length
    let done = 0
    setProgress({ done: 0, total })
    setSaving(true)
    try {
      for (const filesChunk of chunks) {
        await bulkEditFiles({ fileIds: filesChunk, updates })
        done += filesChunk.length
        setProgress({ done, total })
      }
      toast.success(`Updated ${total} file${total === 1 ? '' : 's'}.`)
      refreshFiles()
      handleClose()
    } catch (err) {
      if (done > 0) refreshFiles()
      toast.error(done > 0
        ? `Updated ${done} of ${total} files before an error — please retry the rest.`
        : 'Could not update the files. Please try again.')
    } finally {
      setSaving(false)
      setProgress({ done: 0, total: 0 })
    }
  }

  return (
    <Modal
      title={`${safeSelectedFiles.length} file${safeSelectedFiles.length > 1 ? 's' : ''} selected for editing`}
      open={open}
      onClose={handleClose}
    >
      <FormWrapper>
        <FileTypeLabel>File type</FileTypeLabel>
        <FileTypeWrapper>
          <EnhancedFileTypeSelector
            value={modalValues.fileTypeId}
            onSelect={handleFileTypeSelect}
            placeholder="Search and select file type..."
            autoFocus={shouldAutoFocus}
          />
        </FileTypeWrapper>
        <Input
          label='Expiry date'
          value={modalValues.expirationDate}
          name='expirationDate'
          type='date'
          optional
          onChange={handleValueChange}
        />
        <Checkbox
          label='File expiry reminder'
          name='wantsReminder'
          disabled={!isNotNilOrEmpty(modalValues.expirationDate)}
          value={wantsReminder}
          onChange={() => setWantsReminder(prev => !prev)}
        />
        <Input
          label='Expiration reminder date'
          disabled={!wantsReminder}
          value={modalValues.expirationReminderDate}
          name='expirationReminderDate'
          type='date'
          onChange={handleValueChange}
          inputProps={{ min: moment().add(1, 'day').format('YYYY-MM-DD') }}
        />
        <Checkbox
          label='Set file expiry reminder'
          name='wantsReminder'
          value={wantsReminder}
          onChange={() => setWantsReminder(prev => !prev)}
        />
        {saving && progress.total > 1 && (
          <ProgressRow>
            <ProgressBar>
              <ProgressFill $pct={Math.round((progress.done / progress.total) * 100)} />
            </ProgressBar>
            <ProgressText>{progress.done} of {progress.total} updated</ProgressText>
          </ProgressRow>
        )}
        <UploadButton onClick={handleUpload} disabled={!isValid || saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </UploadButton>
      </FormWrapper>
    </Modal>
  )
}

export default BulkEditFileModal

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 80%;
  margin: 4px auto 12px;
`

const ProgressBar = styled.div`
  flex: 1;
  height: 8px;
  background: #eef0f3;
  border-radius: 999px;
  overflow: hidden;
`

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 999px;
  transition: width 0.25s ease;
`

const ProgressText = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #667085;
  white-space: nowrap;
`

const FormWrapper = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0 20px;
`

const UploadButton = styled.button`
  background-color: transparent;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.secondary};
  height: 30px;
  width: 80%;
  display: block;
  margin: 20px auto 0;
  transition: all 0.3s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.white};
  }

  ${({ disabled, theme }) =>
    disabled &&
    css`
      border-color: ${theme.colors.grey[700]};
      color: ${theme.colors.grey[700]};

      &:hover {
        background-color: transparent;
        color: ${theme.colors.grey[700]};
      }
    `}
`

const FileTypeWrapper = styled.div`
  margin-bottom: 20px;
`

const FileTypeLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 8px;
`
